import { useMemo, useState } from 'react';
import { Empty, ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  AttributedBy,
  GeneratedReview as Report,
  ReviewIssue,
  ReviewMeasurable,
  ReviewRock,
  ReviewTodo,
  ReviewUnit,
} from '../../lib/types';

/**
 * The business review, read off the app rather than typed into it.
 *
 * One section per business unit: the OML maturity its DEPARTMENT was last
 * assessed at, this quarter's rocks, the open issues, the outstanding to-dos
 * and the scorecard. Nothing here is stored — the review IS the state of the
 * app at the moment it was asked for, which is why it has a generated-at stamp
 * rather than a period you pick.
 *
 * THE MATURITY FIGURE IS THE ONE PLACE THE TWO CUTS OF THE BUSINESS MEET: the
 * assessment scores departments (migration 183), the work is attributed to
 * units. So units sharing a department show the same number, each section head
 * names the department it came from, and the headline averages the departments
 * rather than the units — see the summary below.
 *
 * EXPORT IS THE BROWSER'S PRINT DIALOG, which is how the compliance
 * assessment, the charter and the AM SOW already produce documents in this
 * app. It costs no dependency, it gets real selectable-text PDF rather than a
 * screenshot, and the page's own print rules decide what a page break may cut
 * through. "Save as PDF" is a destination in that dialog on every platform
 * this is used on.
 */
export function GeneratedReview() {
  const report = useResource<Report>('/reviews/business-review');
  const [assigning, setAssigning] = useState(false);
  /* Summary by default, including for the export. A full review of eleven
     units runs to a couple of dozen pages of tables, and the thing anybody
     actually circulates is the front of it. The detail has not gone anywhere —
     it is one click, and the click prints too. */
  const [detail, setDetail] = useState(false);

  if (report.loading) return <Loading label="Reading the app" />;
  if (report.error) return <ErrorBanner message={report.error} />;
  if (!report.data) return null;

  const r = report.data;
  const orphans =
    r.Unattributed.Rocks.length +
    r.Unattributed.Issues.length +
    r.Unattributed.Todos.length +
    r.Unattributed.Measurables.length;

  return (
    <div className="bizreview">
      <div className="row-between wrap no-print" style={{ marginBottom: 'var(--space-5)' }}>
        <p className="xs muted">
          Generated {formatDate(r.GeneratedUtc)} · rocks for {r.Quarter} · scorecard week of{' '}
          {formatDate(r.WeekStart)}
        </p>
        <div className="row">
          {orphans > 0 && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAssigning(true)}>
              Attribute {orphans} {orphans === 1 ? 'item' : 'items'}
            </button>
          )}
          <div className="status-group" role="group" aria-label="How much to show">
            <button
              type="button"
              className={`status-btn${detail ? '' : ' met on'}`}
              onClick={() => setDetail(false)}
            >
              Summary
            </button>
            <button
              type="button"
              className={`status-btn${detail ? ' met on' : ''}`}
              onClick={() => setDetail(true)}
            >
              Full detail
            </button>
          </div>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
            Export as PDF
          </button>
        </div>
      </div>

      {/* Print-only, because on screen the same facts are in the bar above. A
          printed document that does not say when it was produced is a
          document nobody can date six months later. */}
      <header className="bizreview-cover print-only">
        <h1>Business Review</h1>
        <p>
          Generated {formatDate(r.GeneratedUtc)} · rocks for {r.Quarter} · scorecard week of{' '}
          {formatDate(r.WeekStart)}
        </p>
      </header>

      <Scoreboard report={r} />

      {detail && <Contents units={r.Units} />}

      {detail && r.Units.map((u) => <UnitSection key={u.BusinessUnitId} unit={u} />)}

      {detail && orphans > 0 && (
        <section className="bizreview-unit">
          <h2>Unattributed</h2>
          <p className="muted small">
            {orphans} {orphans === 1 ? 'item is' : 'items are'} not tagged to a unit and could not be
            derived from an owner&rsquo;s unit. They are listed here rather than left out — a
            review that silently omits an open issue is worse than one that admits it does not know
            whose it is.
          </p>
          <UnitBody
            rocks={r.Unattributed.Rocks}
            issues={r.Unattributed.Issues}
            todos={r.Unattributed.Todos}
            measurables={r.Unattributed.Measurables}
          />
        </section>
      )}

      {assigning && (
        <AttributionDialog
          report={r}
          onClose={() => setAssigning(false)}
          onSaved={() => {
            setAssigning(false);
            report.reload();
          }}
        />
      )}
    </div>
  );
}

/* ── The summary ──────────────────────────────────────────────────────────

   MEASURED, NOT SCORED. Every bar here shows a composition that exists in the
   data — rocks complete against rocks committed, measurables hit against
   measurables read. None of them is a number graded against a threshold
   somebody invented, because a made-up "80% is green" turns a fact into an
   opinion and hides which one it is.

   That is also why the colour means what it means. Navy is the part that
   happened; crimson is the part that did not. Both are counted, and the count
   is printed next to the bar — the brand ladder is the app's own severity
   ramp, and nothing here is legible by colour alone. */

/** A ratio as a bar: what happened, what did not, and the rest still open. */
function Bar({ done, missed, total }: { done: number; missed: number; total: number }) {
  if (total === 0) return <div className="bizr-bar empty" aria-hidden="true" />;
  const pct = (n: number) => `${(n / total) * 100}%`;
  return (
    <div className="bizr-bar" aria-hidden="true">
      {done > 0 && <span className="done" style={{ width: pct(done) }} />}
      {missed > 0 && <span className="missed" style={{ width: pct(missed) }} />}
    </div>
  );
}

/** One headline number, with the bar underneath that produced it. */
function Tile({
  label,
  value,
  foot,
  done,
  missed,
  total,
}: {
  label: string;
  value: string;
  foot: string;
  done?: number;
  missed?: number;
  total?: number;
}) {
  return (
    <div className="bizr-tile">
      <div className="label">{label}</div>
      <div className="figure">{value}</div>
      {total !== undefined && <Bar done={done ?? 0} missed={missed ?? 0} total={total} />}
      <div className="foot">{foot}</div>
    </div>
  );
}

function Scoreboard({ report: r }: { report: Report }) {
  const units = r.Units;
  const all = <T,>(pick: (u: ReviewUnit) => T[]) => [
    ...units.flatMap(pick),
    ...pick(r.Unattributed as unknown as ReviewUnit),
  ];

  const rocks = all((u) => u.Rocks);
  const measurables = all((u) => u.Measurables);
  const issues = all((u) => u.Issues);
  const todos = all((u) => u.Todos);

  const rocksDone = rocks.filter((k) => k.Status === 'complete').length;
  const hits = measurables.filter((m) => m.LastHit === true).length;
  const misses = measurables.filter((m) => m.LastHit === false).length;
  const overdue = todos.filter((t) => t.Overdue).length;

  /* Averaged across the AREAS that have been assessed, not across all of them:
     counting an unassessed one as zero would report the org as less mature for
     having a part of the business nobody has scored yet.

     PER AREA, NOT PER UNIT, and for the reason the per-department version of
     this gave. Two units may sit on one area; averaging the units would count
     that area's figure twice and quietly weight the headline towards whichever
     part of the business happens to be split into the most units. Keying the
     map by area collapses them back to one. */
  const byArea = new Map<number, number>();
  for (const u of units) {
    const score = u.Oml?.Score;
    if (u.AreaId !== null && score !== null && score !== undefined) {
      byArea.set(u.AreaId, score);
    }
  }
  const scored = [...byArea.values()];
  const oml = scored.length ? scored.reduce((a, b) => a + b, 0) / scored.length : null;

  return (
    <section className="bizr-summary">
      <div className="bizr-tiles">
        <Tile
          label="Maturity"
          value={oml === null ? '—' : oml.toFixed(1)}
          foot={`OML, mean of ${scored.length} assessed ${scored.length === 1 ? 'section' : 'sections'} · out of 5`}
          done={oml ?? 0}
          total={5}
        />
        <Tile
          label="Rocks complete"
          value={`${rocksDone}/${rocks.length}`}
          foot={`${r.Quarter} · ${rocks.length - rocksDone} still running`}
          done={rocksDone}
          total={rocks.length}
        />
        <Tile
          label="Measurables hit"
          value={`${hits}/${hits + misses}`}
          foot={`week of ${formatDate(r.WeekStart)} · ${misses} missed`}
          done={hits}
          missed={misses}
          total={hits + misses}
        />
        <Tile
          label="Open issues"
          value={String(issues.length)}
          foot={`${todos.length} to-dos${overdue > 0 ? `, ${overdue} overdue` : ''}`}
        />
      </div>

      <table className="table bizr-scoreboard">
        <thead>
          <tr>
            <th>Unit</th>
            <th className="num">OML</th>
            <th>Rocks</th>
            <th>Measurables</th>
            <th className="num">Issues</th>
            <th className="num">To-dos</th>
          </tr>
        </thead>
        <tbody>
          {units.map((u) => {
            const done = u.Rocks.filter((k) => k.Status === 'complete').length;
            const hit = u.Measurables.filter((m) => m.LastHit === true).length;
            const miss = u.Measurables.filter((m) => m.LastHit === false).length;
            const late = u.Todos.filter((t) => t.Overdue).length;
            const quiet =
              u.Rocks.length + u.Issues.length + u.Todos.length + u.Measurables.length === 0;

            return (
              <tr key={u.BusinessUnitId} className={quiet ? 'bizr-quiet' : undefined}>
                <td className="strong">{u.Name}</td>
                <td className="num tabular">{u.Oml?.Score?.toFixed(1) ?? '—'}</td>
                <td>
                  {u.Rocks.length === 0 ? (
                    <span className="sub">—</span>
                  ) : (
                    <div className="bizr-cell">
                      <span className="tabular">
                        {done}/{u.Rocks.length}
                      </span>
                      <Bar done={done} missed={0} total={u.Rocks.length} />
                    </div>
                  )}
                </td>
                <td>
                  {hit + miss === 0 ? (
                    <span className="sub">—</span>
                  ) : (
                    <div className="bizr-cell">
                      <span className="tabular">
                        {hit}/{hit + miss}
                      </span>
                      <Bar done={hit} missed={miss} total={hit + miss} />
                    </div>
                  )}
                </td>
                <td className="num tabular">{u.Issues.length || <span className="sub">—</span>}</td>
                <td className="num tabular">
                  {u.Todos.length === 0 ? (
                    <span className="sub">—</span>
                  ) : (
                    <>
                      {u.Todos.length}
                      {/* On its own line: "1 1 late" read as a typo. */}
                      {late > 0 && <div className="bizr-late">{late} late</div>}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="xs subtle bizr-key">
        Bars show what happened against what was committed or read — navy complete or hit, crimson
        missed. Nothing here is graded against a target.
      </p>
    </section>
  );
}

/** A contents page, so a printed review of eleven units is navigable. */
function Contents({ units }: { units: ReviewUnit[] }) {
  return (
    <section className="bizreview-contents">
      <h2>Contents</h2>
      <ol>
        {units.map((u) => (
          <li key={u.BusinessUnitId}>
            <span className="name">{u.Name}</span>
            <span className="xs muted">
              {u.Rocks.length} rocks · {u.Issues.length} issues · {u.Todos.length} to-dos ·{' '}
              {u.Measurables.length} measurables
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function UnitSection({ unit: u }: { unit: ReviewUnit }) {
  const empty =
    u.Rocks.length === 0 &&
    u.Issues.length === 0 &&
    u.Todos.length === 0 &&
    u.Measurables.length === 0;

  return (
    <section className="bizreview-unit">
      <div className="bizreview-unit-head">
        <div className="grow">
          <h2>{u.Name}</h2>
          {u.Description && <p className="muted small">{u.Description}</p>}
        </div>
        {u.Oml && u.Oml.Score !== null ? (
          <div className="bizreview-oml">
            <span className="figure">{u.Oml.Score.toFixed(1)}</span>
            <span className="xs muted">
              OML · {u.Oml.SectionName}
              {u.Oml.PeriodLabel ? ` · ${u.Oml.PeriodLabel}` : ''}
            </span>
            {/* THE TWO WAYS THIS FIGURE CAN BE ABOUT MORE THAN THIS UNIT, said
                beside the number and not in a footnote: a reader comparing
                units has to know which of them are not comparable. Neither
                happens on today's data and both are one edit away, because
                nothing in the schema forbids either. */}
            {u.Oml.Sections > 1 && (
              <span className="xs muted">
                mean of {u.Oml.Sections} sections on {u.AreaName}
              </span>
            )}
            {u.AreaUnits > 1 && (
              <span className="xs muted">
                shared with {u.AreaUnits - 1} other {u.AreaUnits === 2 ? 'unit' : 'units'}
              </span>
            )}
          </div>
        ) : (
          /* WHY THERE IS NO NUMBER. The two reasons are fixed on two different
             screens, and a bare em dash sends a reader to neither. */
          <div className="bizreview-oml">
            <span className="xs muted">
              {u.AreaId === null
                ? 'No maturity · no business area on this unit'
                : `No maturity · no OML section scores ${u.AreaName}`}
            </span>
          </div>
        )}
      </div>

      {/* THE ROUTES A READER CAN ACTUALLY TAKE. Attribution still falls back to
          the owner's own unit, but nothing sets that column any more, so
          sending anyone to the Employees screen would be sending them nowhere.
          The two live routes are the item's own tag and the meeting's unit. */}
      {empty ? (
        <p className="bizreview-none">
          Nothing attributed to this unit.
          {u.EmployeeCount === 0 &&
            ' Nobody is on it either, so nothing derives in through an owner — tag items to it here, or put this unit on the meeting in EOS Teams and what is raised there derives into it.'}
        </p>
      ) : (
        <UnitBody
          rocks={u.Rocks}
          issues={u.Issues}
          todos={u.Todos}
          measurables={u.Measurables}
        />
      )}
    </section>
  );
}

function UnitBody({
  rocks,
  issues,
  todos,
  measurables,
}: {
  rocks: ReviewRock[];
  issues: ReviewIssue[];
  todos: ReviewTodo[];
  measurables: ReviewMeasurable[];
}) {
  return (
    <>
      {rocks.length > 0 && (
        <Block title="Rocks" count={rocks.length}>
          <table className="table bizreview-table">
            <thead>
              <tr>
                <th>Rock</th>
                <th>Owner</th>
                <th>Quarter</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rocks.map((x) => (
                <tr key={x.RockId}>
                  <td className="strong">
                    {x.Title}
                    {x.CarriedOver && <span className="badge badge-warning">carried over</span>}
                    <Source how={x.AttributedBy} />
                  </td>
                  <td>{x.OwnerName ?? '—'}</td>
                  <td className="mono">{x.Quarter ?? '—'}</td>
                  <td>
                    <StatusChip status={x.Status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      )}

      {issues.length > 0 && (
        <Block title="Issues" count={issues.length}>
          <table className="table bizreview-table">
            <thead>
              <tr>
                <th>Issue</th>
                <th>Owner</th>
                <th>Term</th>
                <th>Priority</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((x) => (
                <tr key={x.IssueId}>
                  <td className="strong">
                    {x.Title}
                    <Source how={x.AttributedBy} />
                  </td>
                  <td>{x.OwnerName ?? '—'}</td>
                  <td>{x.Term}</td>
                  <td>
                    <StatusChip status={x.Priority} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      )}

      {todos.length > 0 && (
        <Block title="To-dos" count={todos.length}>
          <table className="table bizreview-table">
            <thead>
              <tr>
                <th>To-do</th>
                <th>Owner</th>
                <th>Team</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {todos.map((x) => (
                <tr key={x.TodoId}>
                  <td className="strong">
                    {x.Title}
                    {x.Overdue && <span className="badge badge-critical">overdue</span>}
                    <Source how={x.AttributedBy} />
                  </td>
                  <td>{x.OwnerName ?? '—'}</td>
                  <td>{x.TeamName}</td>
                  <td className="mono">{x.DueDate ? formatDate(x.DueDate) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      )}

      {measurables.length > 0 && (
        <Block title="Scorecard" count={measurables.length}>
          <table className="table bizreview-table">
            <thead>
              <tr>
                <th>Measurable</th>
                <th>Owner</th>
                <th className="num">Goal</th>
                <th className="num">Last week</th>
                <th className="num">13-week</th>
              </tr>
            </thead>
            <tbody>
              {measurables.map((x) => (
                <tr key={x.MeasurableId}>
                  <td className="strong">
                    {x.Name}
                    <Source how={x.AttributedBy} />
                  </td>
                  <td>{x.OwnerName ?? '—'}</td>
                  <td className="num mono">
                    {x.Comparison} {formatMeasure(x.Goal, x.Unit)}
                  </td>
                  <td className="num mono">
                    {x.LastValue === null ? (
                      <span className="subtle">—</span>
                    ) : (
                      <span className={x.LastHit ? 'hit' : 'miss'}>
                        {formatMeasure(x.LastValue, x.Unit)}
                      </span>
                    )}
                  </td>
                  {/* Both numbers, not a percentage: "9/13" says how much of
                      the window was even scored, and "69%" does not. */}
                  <td className="num mono">
                    {x.Scored13 === 0 ? (
                      <span className="subtle">—</span>
                    ) : (
                      `${x.Hit13 ?? 0}/${x.Scored13}`
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Block>
      )}
    </>
  );
}

function Block({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bizreview-block">
      <h3>
        {title} <span className="xs muted mono">{count}</span>
      </h3>
      {children}
    </div>
  );
}

/**
 * Whether this item was tagged or inferred.
 *
 * Only inferred ones are marked. A tagged item is somebody's judgement and
 * needs no apology; a derived one is this query's guess from the owner's
 * department, and a reader deciding what to do about a rock should be able to
 * see which of the two they are looking at. Print keeps it — the distinction
 * matters more in a document nobody can interrogate than on a screen.
 */
/**
 * How this item found its unit, shown only when nobody said so directly.
 *
 * A tag is a decision and needs no label. The other two are inferences and
 * should not look like decisions: 'meeting' is the unit set on the EOS meeting
 * the item was raised in, 'owner' is a fall back to wherever its owner sits —
 * the weakest of the three, and the one most worth a reader's doubt.
 */
function Source({ how }: { how: AttributedBy }) {
  if (how === 'meeting') {
    return (
      <span className="bizreview-derived" title="From the unit set on its EOS meeting">
        via meeting
      </span>
    );
  }
  if (how !== 'owner') return null;
  return (
    <span className="bizreview-derived" title="Derived from the owner's business unit">
      derived
    </span>
  );
}

function StatusChip({ status }: { status: string }) {
  const tone =
    status === 'complete' || status === 'on_track'
      ? 'badge-good'
      : status === 'off_track' || status === 'high'
        ? 'badge-danger'
        : status === 'medium' || status === 'in_progress'
          ? 'badge-warning'
          : 'badge-neutral';
  return <span className={`badge ${tone}`}>{status.replace(/_/g, ' ')}</span>;
}

function formatMeasure(value: number, unit: string): string {
  const n = Number(value);
  if (unit === '$') return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (unit === '%') return `${n}%`;
  return String(n);
}

/* =========================================================================
   Attribution
   ========================================================================= */

type Kind = 'rock' | 'issue' | 'todo' | 'measurable';
interface Orphan {
  kind: Kind;
  id: number;
  title: string;
  owner: string | null;
  context: string;
}

/**
 * Tag the items nothing could place.
 *
 * One dialog over all four kinds, because "these belong to Service Desk" is
 * one thought and making somebody have it four times in four screens is how
 * attribution never gets done. Choices are staged locally and sent in a
 * single request when saved, so a half-finished pass changes nothing.
 */
function AttributionDialog({
  report,
  onClose,
  onSaved,
}: {
  report: Report;
  onClose: () => void;
  onSaved: () => void;
}) {
  const orphans = useMemo<Orphan[]>(
    () => [
      ...report.Unattributed.Rocks.map((x) => ({
        kind: 'rock' as const,
        id: x.RockId,
        title: x.Title,
        owner: x.OwnerName,
        context: `Rock · ${x.Quarter ?? 'no quarter'}`,
      })),
      ...report.Unattributed.Issues.map((x) => ({
        kind: 'issue' as const,
        id: x.IssueId,
        title: x.Title,
        owner: x.OwnerName,
        context: `Issue · ${x.Priority}`,
      })),
      ...report.Unattributed.Todos.map((x) => ({
        kind: 'todo' as const,
        id: x.TodoId,
        title: x.Title,
        owner: x.OwnerName,
        context: `To-do · ${x.TeamName}`,
      })),
      ...report.Unattributed.Measurables.map((x) => ({
        kind: 'measurable' as const,
        id: x.MeasurableId,
        title: x.Name,
        owner: x.OwnerName,
        context: `Measurable · ${x.TeamName}`,
      })),
    ],
    [report],
  );

  const [picked, setPicked] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = (o: Orphan) => `${o.kind}:${o.id}`;
  const chosen = Object.keys(picked).length;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put('/reviews/attribution', {
        assignments: Object.entries(picked).map(([k, businessUnitId]) => {
          const [kind, id] = k.split(':');
          return { kind, id: Number(id), businessUnitId };
        }),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal wide" role="dialog" aria-modal="true" aria-label="Attribute items">
        <div className="modal-head">
          <div>
            <h2>Attribute to a business unit</h2>
            <div className="sub">
              {orphans.length} {orphans.length === 1 ? 'item has' : 'items have'} no unit, and no
              owner assigned to one to derive it from.
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="modal-body">
          {error && <ErrorBanner message={error} />}
          {orphans.length === 0 ? (
            <Empty title="Everything is attributed" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Owner</th>
                  <th>Business unit</th>
                </tr>
              </thead>
              <tbody>
                {orphans.map((o) => (
                  <tr key={key(o)}>
                    <td>
                      <div className="strong">{o.title}</div>
                      <div className="xs muted">{o.context}</div>
                    </td>
                    <td className="xs">{o.owner ?? '—'}</td>
                    <td>
                      <select
                        className="select"
                        value={picked[key(o)] ?? ''}
                        onChange={(e) =>
                          setPicked((p) => {
                            const next = { ...p };
                            if (e.target.value === '') delete next[key(o)];
                            else next[key(o)] = Number(e.target.value);
                            return next;
                          })
                        }
                      >
                        <option value="">Leave unattributed</option>
                        {report.Units.map((u) => (
                          <option key={u.BusinessUnitId} value={u.BusinessUnitId}>
                            {u.Name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || chosen === 0}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : `Attribute ${chosen}`}
          </button>
        </div>
      </div>
    </div>
  );
}
